<template>
  <article class="notification-card" :class="{ 'notification-card--leida': leida }">
    <div class="notification-card__icon" aria-hidden="true">
      <i :class="['fa-lg', iconClass]"></i>
    </div>
    <div class="notification-card__main">
      <div class="notification-card__title">
        <span class="notification-card__title-text">{{ notificacion.Titulo }}</span>
 <time class="notification-card__time" :datetime="String(notificacion.FechaCreacion)">{{
          formattedFechaCreacion
        }}</time>

      </div>
      
      <div class="notification-card__message">{{ notificacion.Mensaje }}</div>
    
      <div class="notification-card__footer">
         <a
        v-if="origenEnlace.href"
        class="notification-card__link btn px-0"
        :href="origenEnlace.href"
        target="_blank"
        rel="noopener noreferrer"
      ><i class="fa-solid fa-up-right-from-square text-primary me-2"style="cursor: pointer;"></i>
        {{ $t(origenEnlace.labelKey) }}
      </a>
        <button
          v-if="!leida"
          type="button"
          class="notification-card__mark-read"
          :title="$t('notificaciones.marcar_leida')"
          @click="marcarLeido"
        >
          <i class="fa-regular fa-envelope-open" aria-hidden="true" />
          {{ $t("notificaciones.marcar_leido_boton") }}
        </button>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { RouteLocationRaw } from "vue-router";
import type { NotificacionModel } from "@api/api";
import type { PropType } from "vue";
import { useApi } from "@api/integration";
import { useRouter } from "@/.shared/services/routing-services";
import { useIntl } from "@/.shared/services/i18n";

const props = defineProps({
  notificacion: {
    type: Object as PropType<NotificacionModel>,
    required: true,
  },
  leida: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  "notificacion-leida": [];
}>();

const api = useApi();
const router = useRouter();
const intl = useIntl();

/** Primera referencia con esquema conocido → ruta con nombre (para i18Route). */
function esquemaReferencia(raw: string): string | null {
  const sep = raw.indexOf(":");
  if (sep <= 0) return null;
  const id = raw.slice(sep + 1).trim();
  if (!id) return null;
  return raw.slice(0, sep).toLowerCase();
}

function idDespuesDeEsquema(raw: string): string | null {
  const sep = raw.indexOf(":");
  if (sep <= 0) return null;
  const id = raw.slice(sep + 1).trim();
  return id || null;
}

function referenciaARuta(raw: string): RouteLocationRaw | null {
  const sep = raw.indexOf(":");
  if (sep <= 0) return null;
  const scheme = raw.slice(0, sep).toLowerCase();
  const id = raw.slice(sep + 1).trim();
  if (!id) return null;
  if (scheme === "ticket") return { name: "detalle-ticket-view", params: { id } };
  if (scheme === "pds") return { name: "pds-view" };
  if (scheme === "actuacion") return { name: "detalle-actuacion-view", params: { id } };
  if (scheme === "ocaubicacion") return { name: "oca-detalle-ubicacion-view", params: { id } };
  if (scheme === "ocact") return { name: "oca-detalle-centro-transformacion-view", params: { id } };
  if (scheme === "transaccion") return { name: "transaccion-detalle-view", params: { id } };
  if (scheme === "partner") return { name: "partner-detalle-view", params: { id } };
  return null;
}

/** Detalle WO: ruta `detalle-wo/:id/:idActuacion` (requiere referencias `wo:` y `actuacion:`). */
function rutaDetalleWoDesdeReferencias(refs: string[]): RouteLocationRaw | null {
  const rawWo = refs.find((r) => esquemaReferencia(r) === "wo");
  const rawActuacion = refs.find((r) => esquemaReferencia(r) === "actuacion");
  if (!rawWo || !rawActuacion) return null;
  const idWo = idDespuesDeEsquema(rawWo);
  const idActuacion = idDespuesDeEsquema(rawActuacion);
  if (!idWo || !idActuacion) return null;
  return { name: "detalle-wo-view", params: { id: idWo, idActuacion } };
}

/** Href + clave i18n del texto del enlace según el primer origen reconocido. */
const origenEnlace = computed(() => {
  // Prestador (PDS): no hay ficha útil en el portal; ocultamos el enlace.
  if (props.notificacion.Tipo.startsWith("Pds."))
    return { href: "", labelKey: "notificaciones.ver_origen" };

  const refs = props.notificacion.Referencias ?? [];
  const locWo = rutaDetalleWoDesdeReferencias(refs);
  if (locWo) {
    const localized = intl.i18Route(locWo);
    const href = router.resolve(localized).href;
    return { href, labelKey: "notificaciones.enlace_ir_wo" };
  }
  for (const r of refs) {
    const loc = referenciaARuta(r);
    if (!loc) continue;
    const scheme = esquemaReferencia(r);
    let labelKey = "notificaciones.ver_origen";
    if (scheme === "ticket") labelKey = "notificaciones.enlace_ir_ticket";
    else if (scheme === "actuacion") labelKey = "notificaciones.enlace_ir_actuacion";
    else if (scheme === "ocaubicacion" || scheme === "ocact")
      labelKey = "notificaciones.enlace_ir_oca";
    else if (scheme === "transaccion")
      labelKey = "notificaciones.enlace_ir_informe";
    else if (scheme === "partner")
      labelKey = "notificaciones.enlace_ir_partner";
    const localized = intl.i18Route(loc);
    const href = router.resolve(localized).href;
    return { href, labelKey };
  }
  return { href: "", labelKey: "notificaciones.ver_origen" };
});

const formattedFechaCreacion = computed(() => {
  const d = new Date(props.notificacion.FechaCreacion as unknown as string);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
});

const iconClass = computed(() => {
  const categoria = props.notificacion.Tipo.split(".")[0];
  switch (categoria) {
    case "Ticket":
      return "fa-regular fa-triangle-exclamation";
    case "Pds":
      return "fa-regular fa-building";
    case "Actuacion":
      return "fa-regular fa-clipboard-list";
    case "Wo":
      return "fa-regular fa-screwdriver-wrench";
    case "Oca":
      return "fa-regular fa-clipboard-check";
    case "Revenue":
      return "fa-regular fa-file-invoice-dollar";
    default:
      return "fa-regular fa-bell";
  }
});

const marcarLeido = async () => {
  await api.callStandardApi("readNotificacion", { showLoading: false }, props.notificacion.Id);
  emit("notificacion-leida");
};
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/_themevars.scss";
@import "@/styles/figma.scss";


.notification-card {
  display: flex;
  padding: var(--spacing-sm, 12px);
  align-items: flex-start;
  gap: var(--spacing-xs, 8px);
  align-self: stretch;
  border-radius: 10px;
  border: var(--border-widht-sm, 1px) solid var(--border-default-default, #C2CDDD);
  background: var(--background-normal-default, #FFF);

  &:hover,
  &:focus-within {
    border-color: var(--border-default-hover, #d1d5db);
    box-shadow: 0 2px 8px rgba(14, 20, 26, 0.06);
  }
  &--leida {
  background: var(--background-default-page, #f8fafd); /* O cambia por el fondo de tu preferencia para leída */
}
}



.notification-card__icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-default-caption)
}

.notification-card__main {
  flex: 1;  
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
}

.notification-card__title {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs, 4px);
  margin-top: var(--spacing-2xs, 4px);

  .notification-card__title-text{
    color: var(--text-primary-default, #D3135A);
    font-size: var(--font-size-body-md, 14px);
    font-weight: 900;

  }
}

.notification-card__message {
color: var(--text-default-default, #000);
font-size: var(--font-size-body-sm, 12px);
line-height: var(--line-height-body-sm, 18px);
}

.notification-card__link {
  text-decoration: none;
  font-size: var(--font-size-body-sm, 12px);
  color: var(--text-default-default, #000);
}

.notification-card__link:hover {
  text-decoration: underline;
}

.notification-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 32px;
}

.notification-card__time {
  font-size: var(--font-size-body-sm, 12px);
  color: var(--text-default-caption, #6b7280);
}

.notification-card__mark-read {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
  padding: 6px 12px;
  border-radius: 8px;
  border: 0px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary-default, #d3135a);
  cursor: pointer;
  transition: opacity 0.15s ease, background 0.15s ease, color 0.15s ease;
  
  /* Visible en móvil/tablet */
  opacity: 1;
  visibility: visible;
  pointer-events: auto;

  &:hover {
    background: var(--background-primary-alt-hover);
  }

  /* Oculto por defecto en PC, solo se muestra con hover de la card */
  @media (hover: hover) and (pointer: fine) {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;

    &:hover {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
  }
}

@media (hover: hover) and (pointer: fine) {
  .notification-card:hover .notification-card__mark-read,
  .notification-card:focus-within .notification-card__mark-read {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
}
</style>
